import React from 'react';
import { Card, Col, Image, Row, Select, Spin, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ClientSearchResult } from 'api/useClient';
import type { MappingHeaderProps } from '../types';
import { getLogoUrl } from 'shared/constants';
import fallbackLogo from 'assets/fallbackLogo.png';

/**
 * Renders the top section of the Supplier Mapping page.
 *
 * Contains the client search Select (with debounced search and clear support)
 * and — once a client with a domain is selected — the client logo aligned to the right.
 * @param {MappingHeaderProps} props - Component props.
 * @param {boolean} props.loading - `true` while a client search request is in-flight.
 * @param {ClientSearchResult[]} props.results - Current list of client search results.
 * @param {ClientSearchResult | undefined} props.selectedClient - Currently selected client, or `undefined`.
 * @param {Function} props.onSearch - Fires when the user types in the search box.
 * @param {Function} props.onSelect - Fires when the user picks a client from the dropdown.
 * @param {Function} props.onClear - Fires when the user clears the selection.
 * @returns {React.ReactElement} The header card with client search and optional client info strip.
 */
const MappingHeader: React.FC<MappingHeaderProps> = ({
  loading,
  results,
  selectedClient,
  onSearch,
  onSelect,
  onClear
}) => (
  <Card className="pt-4">
    <Row>
      <Col>
        <Typography.Title level={5}>Search Clients</Typography.Title>
      </Col>
    </Row>

    <Row justify="space-between" align="middle">
      <Col>
        <Select<number, ClientSearchResult>
          placeholder="Search by client name"
          size="large"
          style={{ width: 480 }}
          autoFocus
          allowClear
          showSearch
          filterOption={false}
          notFoundContent={loading ? <Spin size="default" /> : null}
          suffixIcon={<SearchOutlined />}
          fieldNames={{ label: 'name', value: 'id' }}
          options={results}
          onSearch={onSearch}
          onSelect={onSelect}
          onClear={onClear}
        />
      </Col>
      {selectedClient?.domain && (
        <Col>
          <Tooltip title={`Visit the ${selectedClient.name} website`}>
            <a href={`https://${selectedClient.domain}`} target="_blank" rel="noopener noreferrer">
              <Image
                src={getLogoUrl(selectedClient.domain)}
                fallback={fallbackLogo}
                height={80}
                preview={false}
                style={{ objectFit: 'contain', cursor: 'pointer' }}
              />
            </a>
          </Tooltip>
        </Col>
      )}
    </Row>
  </Card>
);

MappingHeader.displayName = 'MappingHeader';

export default React.memo(MappingHeader);
